import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import TemplateBuilder from "./TeamplteList/index";
import Menu from "./Menu";
import Empty from "./Empty";
import { useSelector, shallowEqual } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import GenerateChartModal from "./GenerateChart"; // Import the modal
import { Button } from "semantic-ui-react";
import { useLanguage } from 'hooks/useLanguage';
import { transformOutcomeQToFrench } from 'utilities/outcomeQTranslations';

const Templates = ({ updateSlides, generatePresentation }) => {
  const { language } = useLanguage();
  const [slides, setSlides] = useState([]); // Each slide is { template: '...', data: { ... } }
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSlideData, setSelectedSlideData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outcomeQ, setOutcomeQ] = useState(null);
  const [outcomeStandards, setOutcomeStandards] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // State to control modal

  const get_organizations = useSelector((state) => state.organizations);
  const core_data = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );
  const get_auth = useSelector((state) => state.auth);
  const get_survey_questions = useSelector((state) => state.surveyquestions);

  const addSlide = (template) => {
    const newSlide = { template, data: {} };
    setSlides([...slides, newSlide]);
    setSelectedSlideIndex(slides.length);
  };

  const removeSlide = (index) => {
    setSlides((prevSlides) => {
      const updatedSlides = prevSlides.filter((_, i) => i !== index);
      return updatedSlides;
    });

    if (slides.length === 1) {
      setSelectedTemplate(null);
      setSelectedSlideIndex(null);
    } else {
      if (selectedSlideIndex === 0) {
        setSelectedSlideIndex(0);
        setSelectedTemplate(slides[0].template);
      } else {
        setSelectedSlideIndex(selectedSlideIndex - 1);
        setSelectedTemplate(slides[selectedSlideIndex - 1].template);
      }
    }
  };

  const updateSlideData = (data) => {
    const updatedSlides = slides.map((slide, index) =>
      index === selectedSlideIndex ? { ...slide, data } : slide
    );
    setSlides(updatedSlides);
    updateSlides(updatedSlides);
    console.log("Updated slides", updatedSlides);
  };

  useEffect(() => {
    if (selectedSlideIndex !== null) {
      const selectedSlide = slides[selectedSlideIndex];
      console.log(slides, selectedSlideIndex, selectedSlide);
      setSelectedTemplate(selectedSlide.template);
      setSelectedSlideData(selectedSlide.data);
    }
  }, [selectedSlideIndex, slides]);

  useEffect(() => {
    if (get_organizations) {
      setOrganizationData(
        get_organizations[
          get_auth?.organization_id || get_organizations?.organization?.id
        ]
      );
    }
  }, [get_organizations]);

  // Transform outcomeQ based on language
  const transformedOutcomeQ = useMemo(() => {
    if (!outcomeQ) return null;

    if (language === 'fr') {
      return transformOutcomeQToFrench(outcomeQ);
    }

    return outcomeQ;
  }, [outcomeQ, language]);

  useEffect(() => {
    if (organizationData) {
      const sortOrder = organizationData?.styling?.survey_sequence?.find(
        (item) => item.value === "outcome_question"
      )?.question?.sort_order;

      if (sortOrder) {
        const outcomeQ = get_survey_questions?.outcome_questions.find(
          (item) => item.sort_order === sortOrder
        );
        console.log(outcomeQ);
        setOutcomeQ(outcomeQ);
      }
    }
  }, [core_data, organizationData]);

  useEffect(() => {
    if (outcomeQ) {
      setOutcomeStandards(
        core_data?.get_standards.find((f) =>
          f.question_sort_order.includes(outcomeQ.sort_order)
        )?.response
      );
    }
  }, [outcomeQ, core_data]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [selectedSlideIndex]);

  // Handle drag and drop reordering
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(slides);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSlides(items);
    if (result.source.index === selectedSlideIndex) {
      setSelectedSlideIndex(result.destination.index);
    } else if (
      result.source.index < selectedSlideIndex &&
      result.destination.index >= selectedSlideIndex
    ) {
      setSelectedSlideIndex(selectedSlideIndex - 1);
    } else if (
      result.source.index > selectedSlideIndex &&
      result.destination.index <= selectedSlideIndex
    ) {
      setSelectedSlideIndex(selectedSlideIndex + 1);
    }
  };

  const getAnchorName = (anchor, categories) => {
    return categories?.find((c) => c.id === anchor)?.name || "Empty slide";
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BlueTitle>AI Toolkit</BlueTitle>
          <BigTitle>Presentation Builder</BigTitle>
        </HeaderLeft>
        <HeaderRight>
          <GenerateButton
            onClick={() => setModalOpen(true)}
            disabled={slides.length === 0}
          >
            Generate Presentation
          </GenerateButton>
        </HeaderRight>
      </Header>

      <Content>
        <SlideOptions>
          <AddSlideContainer>
            <Menu onSelect={(t) => addSlide(t)} />
          </AddSlideContainer>
          <SlideList>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="slides">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {slides.map((slide, index) => (
                      <Draggable
                        key={index}
                        draggableId={`slide-${index}`}
                        index={index}
                      >
                        {(provided) => {
                          const anchorName = getAnchorName(
                            slide.data?.anchor,
                            core_data?.categories?.[0]?.categories
                          );
                          return (
                            <Option
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedSlideIndex(index)}
                              selected={selectedSlideIndex === index}
                            >
                              {slide.template}
                              <FactorTitle>
                                {anchorName
                                  ? anchorName
                                  : slide.data?.title
                                  ? slide.data?.title
                                  : "Empty slide"}
                              </FactorTitle>
                            </Option>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </SlideList>
        </SlideOptions>

        <SelectedTemplate>
          {selectedTemplate && (
            <Remove onClick={() => removeSlide(selectedSlideIndex)}>
              Remove slide
            </Remove>
          )}
          {selectedTemplate ? (
            <TemplateBuilder
              selected={selectedTemplate}
              slideData={selectedSlideData}
              updateSlideData={updateSlideData}
              loading={loading}
              outcomeQ={transformedOutcomeQ}
              outcomeStandards={outcomeStandards}
            />
          ) : (
            <Empty />
          )}
        </SelectedTemplate>
      </Content>

      <GenerateChartModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        generatePresentation={generatePresentation}
        slides={slides}
      />
    </Container>
  );
};

export default Templates;

// Updated Styles

const Container = styled.div`
  padding: 40px;
  background-color: #f4f7fa;
  min-height: 100vh;
  border-radius:10px;
`;

// Header: title on left, generate button on right
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e6ed;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const BlueTitle = styled.h4`
  font-size: 14px;
  color: #2d70e2;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
`;

const BigTitle = styled.h1`
  color: #2a3039;
  font-size: 36px;
  font-weight: 700;
  margin: 5px 0 0;
`;

const GenerateButton = styled(Button)`
  background-color: #2d70e2 !important;
  color: white !important;
  padding: 12px 24px !important;
  border: none !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  font-size: 16px !important;
  &:hover {
    background-color: #1a4a8e !important;
  }
`;

// Content: fixed total width, centered and with a minimum height
const Content = styled.div`
  display: flex;
  gap: 20px;
    margin: 0 auto;
  min-height: 600px;
`;
const SlideOptions = styled.div`
  width: 250px;
  min-width: 250px;
  height: 1000px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #ffffff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  /* Hide scrollbar by default for Firefox */
  scrollbar-width: none;
  /* Hide scrollbar by default for WebKit browsers */
  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }

  /* Show a minimal scrollbar on hover for Firefox */
  &:hover {
    scrollbar-width: thin;
  }
  
  /* Show a minimal scrollbar on hover for WebKit browsers */
  &:hover::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  &:hover::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 3px;
    border: 1px solid transparent;
  }
`;


// Dropdown for adding slides remains always visible at the top
const AddSlideContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e6ed;
  background-color: #f9fafc;
`;

// Slide list becomes scrollable if overflow occurs
const SlideList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
`;

// Each slide option styled as a card with fixed styling
const Option = styled.div`
  width: 100%;
  padding: 12px;
  border: ${(props) =>
    props.selected ? "2px solid #2d70e2" : "1px solid #e0e6ed"};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${(props) => (props.selected ? "#f0f8ff" : "#ffffff")};
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FactorTitle = styled.div`
  font-size: 12px;
  color: #666d79;
  margin-top: 6px;
  font-weight: 400;
`;

// Slide content area: fixed width (800px) and minimum height
const SelectedTemplate = styled.div`
  width: calc(100% - 250px);
  min-height: 600px;
  background-color: #ffffff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Remove = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 12px;
  color: #2d70e2;
  cursor: pointer;
`;
